import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldAlert, KeyRound, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api, { authAPI, clearCsrfToken } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function LicenseRenew() {
  const navigate = useNavigate();
  const { checkSession, user } = useAuth();
  const [licenseKey, setLicenseKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRenew = async (e) => {
    e.preventDefault();
    const key = licenseKey.trim().toUpperCase();
    if (!key) {
      toast.error('Bitte einen Lizenzschlüssel eingeben');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/renew-license', { license_key: key });
      toast.success('Lizenz erfolgreich erneuert!');
      // Refresh /auth/me so license_valid becomes true again
      if (checkSession) await checkSession();
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Lizenz konnte nicht erneuert werden';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch {}
    clearCsrfToken();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="license-renew-card">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-barlow">Lizenz erforderlich</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-sm text-amber-900">
              Ihre Lizenz wurde deaktiviert, gelöscht oder ist abgelaufen.
              Bitte geben Sie einen gültigen Lizenzschlüssel ein, um Ihr Konto weiter zu nutzen.
              Ihre Daten bleiben erhalten.
            </AlertDescription>
          </Alert>

          <form onSubmit={handleRenew} className="space-y-4">
            <div>
              <Label htmlFor="license_key">Lizenzschlüssel</Label>
              <div className="relative mt-1">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="license_key"
                  data-testid="license-renew-input"
                  placeholder="FW-XXXX-XXXX-XXXX"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  className="pl-9 uppercase tracking-wide font-mono"
                  autoFocus
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white"
              disabled={submitting}
              data-testid="license-renew-submit"
            >
              {submitting ? 'Wird geprüft…' : 'Lizenz aktivieren'}
            </Button>
          </form>

          <div className="border-t pt-4 text-center">
            <p className="text-xs text-gray-500 mb-2">
              Angemeldet als <span className="font-medium">{user?.email}</span>
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="license-renew-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Abmelden
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
